/**
 * Static content for the AGNT, Inc. home page (the investor-relations landing).
 *
 * Owned in-repo like the Financials and Resources ports: the legacy landing page
 * is hand-authored Elementor HTML with no structured WordPress source, so the
 * copy/links live here as typed English content, edited via PRs and localised at
 * render by GTranslate. Types stay transport-agnostic so a section can later be
 * swapped for a WP fetch without touching its block component.
 *
 * Design chrome that never lived in WP (section eyebrows, accent ramps, the
 * hero panel shape) stays in the block components; this module carries only the
 * page's substantive content. Each top-level key maps 1:1 to a section block in
 * `./blocks`.
 *
 * The careers section reuses the shared `CardStack` component (the one that
 * powers join.exprealty.com's "What Sets Us Apart"), so its content is typed as
 * the tenant `CardStackData` shape. Strings are plain English — `CardStack`
 * runs them through `t()`, which passes unknown keys through unchanged, so AGNT
 * keeps rendering English for GTranslate (no i18n keys added).
 */

import { Colors } from 'constants/colors';

import type { CardStackData } from 'tenants';
import { AGNT_IR_MAILTO } from '@tenants/definitions/agnt-inc/constants';

export interface CtaLink {
  label: string;
  href: string;
}

// ---------------------------------------------------------------------------
// Hero — full-bleed video with an overlapping glass panel.
// ---------------------------------------------------------------------------
export interface HeroContent {
  /** Brand mark shown in the panel eyebrow slot, e.g. `AGNT, Inc.`. */
  brand: string;
  /** Headline lines, rendered stacked (one per array entry). */
  headlineLines: string[];
  mission: string;
  /** Background video. Public path; falls back to a still navy panel if absent. */
  videoSrc: string;
}

// ---------------------------------------------------------------------------
// Results summary — three columns inside one rounded container.
// ---------------------------------------------------------------------------
/** A labelled row of document links, e.g. `Earnings Release → HTML · PDF`. */
export interface ResultDocRow {
  label: string;
  links: CtaLink[];
}

export interface LatestResultsColumn {
  /** Small heading top line, e.g. `Latest`. */
  kicker: string;
  /** Emphasised heading bottom line, e.g. `Financial Results`. */
  title: string;
  /** Quarter chip, e.g. `Q1 2026`. */
  quarter: string;
  /** Quarter detail, e.g. `Quarter Ended Apr 3, 2026`. */
  quarterDetail: string;
  rows: ResultDocRow[];
}

export interface CompanyUpdatesColumn {
  kicker: string;
  title: string;
  /** Featured update headline. */
  highlight: string;
  primary: CtaLink;
  secondary: CtaLink;
}

export interface UpcomingEventColumn {
  kicker: string;
  title: string;
  /**
   * Next scheduled event, e.g. `when: 'May 11, 2026 · 5:30 a.m. PT'`. Omitted
   * when nothing is scheduled — the column then shows just `viewAll`/`ctas`.
   */
  when?: string;
  eventTitle?: string;
  /** Inline links beneath the event (View Event, Add to Calendar). */
  eventLinks?: CtaLink[];
  viewAll: CtaLink;
  /** Pill CTAs in the column footer. */
  ctas: CtaLink[];
}

export interface ResultsSummary {
  latestResults: LatestResultsColumn;
  companyUpdates: CompanyUpdatesColumn;
  upcomingEvents: UpcomingEventColumn;
}

// ---------------------------------------------------------------------------
// Founder quote — scroll-lit, word-by-word.
// ---------------------------------------------------------------------------
/** A run of words in the quote. `emphasis` runs light up in the brand blue. */
export interface QuoteSegment {
  text: string;
  emphasis?: boolean;
}

export interface FounderQuote {
  tag: string;
  name: string;
  role: string;
  photoSrc: string;
  photoAlt: string;
  /** The quote, split into segments so emphasis can be modelled as data. */
  segments: QuoteSegment[];
}

// ---------------------------------------------------------------------------
// Innovation + Investor Center — intro copy on the left, link rows on the right.
// ---------------------------------------------------------------------------
/** One investor-center link row. Either a big `figure` (stock price) or a `title`. */
export interface InvestorRow {
  tag: string;
  /** Large number/price line (mutually exclusive with `title`). */
  figure?: string;
  title?: string;
  subtitle: string;
  href: string;
  /**
   * Marks the live-quote row. When set, the section fills `figure` with the live
   * AGNT price (from SSR) and appends the delay note to `subtitle`. The static
   * `title`/`subtitle` here are the fallback shown when no quote is available.
   */
  kind?: 'stock';
}

export interface InnovationInvestor {
  eyebrow: string;
  heading: string;
  body: string;
  /** "Learn More" pill CTA below the body copy. */
  cta: CtaLink;
  /** Header above the link rows, with its own "view all" link. */
  rowsLink: CtaLink;
  rows: InvestorRow[];
}

// ---------------------------------------------------------------------------
// News — three-card grid.
// ---------------------------------------------------------------------------
export interface NewsCard {
  date: string;
  title: string;
  excerpt: string;
  href: string;
}

export interface NewsSection {
  eyebrow: string;
  heading: string;
  viewAll: CtaLink;
  // No static `cards` — the section is 100% WP-driven via `fetchLatestPressReleases`.
  // Cards are passed as a separate prop in HomeBlocks; the section is hidden when
  // the fetch returns nothing.
}

// ---------------------------------------------------------------------------
// Careers — intro section above the shared `CardStack` stacking cards.
// ---------------------------------------------------------------------------
export interface CareersIntro {
  eyebrow: string;
  heading: string;
  body: string;
  cta: CtaLink;
}
// Calendar + email alerts — two columns.
// ---------------------------------------------------------------------------
export interface CalendarEvent {
  /** Day number, e.g. `11`, or a short token like `TBD`. */
  day: string;
  /** Month/period line, e.g. `May '26`. */
  month: string;
  title: string;
  /** Optional supporting line (e.g. a time). WP events have no time field. */
  meta?: string;
  /**
   * ISO date (`YYYY-MM-DD`). Past-dated events are filtered out of the calendar.
   * Omit for undated/TBD events (always kept).
   */
  date?: string;
  addToCalendar?: CtaLink;
}

export type AlertsLinkIcon = 'email' | 'linkedin' | 'careers';

export interface AlertsLink {
  icon: AlertsLinkIcon;
  label: string;
  href: string;
}

export interface AlertsForm {
  eyebrow: string;
  heading: string;
  body: string;
  links: AlertsLink[];
}

export interface CalendarAlerts {
  scheduleEyebrow: string;
  scheduleHeading: string;
  /**
   * Static for now — the WP `event` CPT isn't populated with ACF dates yet (one
   * published event, empty ACF). Past-dated entries auto-hide (see `date`), so
   * this stays correct as quarters roll over; revisit a WP fetch once events are
   * published with `start_date`.
   */
  events: CalendarEvent[];
  /** "View All Events" link below the calendar → events page. */
  viewAll: CtaLink;
  form: AlertsForm;
}

// ---------------------------------------------------------------------------
// Page model.
// ---------------------------------------------------------------------------
export interface HomeContent {
  hero: HeroContent;
  resultsSummary: ResultsSummary;
  founderQuote: FounderQuote;
  innovationInvestor: InnovationInvestor;
  news: NewsSection;
  careersIntro: CareersIntro;
  /** Reuses the shared `CardStack` component (join.exprealty.com "What Sets Us Apart"). */
  careers: CardStackData;
  calendarAlerts: CalendarAlerts;
}

// Internal AGNT routes (no domain — served by this app).
const EVENTS_URL = '/events-and-presentations';
const Q2_PRESS_URL = '/press-releases/agnt-inc-reports-q2-2026-results';
const FINANCIALS_URL = '/financials';

// External links — PDFs, YouTube, or pages without an internal AGNT route.
const Q2_EXP_WORLD_URL = 'https://exp.world/earnings';
const Q2_LIVESTREAM_URL = 'https://www.youtube.com/live/Wy49rgulIso';
const Q2_DECK_URL = 'https://content.exprealty.com/share/q2-fy26-agnt-earnings-fireside-chat-final/';
const Q2_10Q_URL = 'https://content.exprealty.com/share/10q-agnt-inc-08-04-2026';

export const HOME_CONTENT: HomeContent = {
  hero: {
    brand: 'AGNT, Inc.',
    headlineLines: ['Built by Agents.', 'Built for Agents.'],
    mission:
      'AGNT, Inc. is a transformative multi-model real estate platform powering agents, franchises, and entrepreneurs across the globe.',
    videoSrc: '/next/assets/videos/agnt-inc/landing-video.mp4',
  },

  resultsSummary: {
    latestResults: {
      kicker: 'Latest',
      title: 'Financial Results',
      quarter: 'Q2 2026',
      quarterDetail: 'Quarter Ended Jun 30, 2026',
      rows: [
        {
          label: 'Earnings Release',
          links: [
            { label: 'HTML', href: Q2_PRESS_URL },
          ],
        },
        {
          label: 'Earnings Call',
          links: [
            { label: 'WEBCAST', href: Q2_LIVESTREAM_URL },
          ],
        },
        {
          label: 'Earnings Presentation',
          links: [{ label: 'PDF', href: Q2_DECK_URL }],
        },
        {
          label: '10-Q',
          links: [
            { label: 'PDF', href: Q2_10Q_URL },
          ],
        },
      ],
    },
    companyUpdates: {
      kicker: 'Company',
      title: 'Updates',
      highlight: 'Second Quarter 2026 Results »',
      primary: { label: 'View Press Release', href: Q2_PRESS_URL },
      secondary: { label: 'View More Company Updates', href: FINANCIALS_URL },
    },
    upcomingEvents: {
      kicker: 'Upcoming',
      title: 'Events & Presentations',
      viewAll: { label: 'View All Events', href: EVENTS_URL },
      ctas: [
        { label: 'Join at exp.world →', href: Q2_EXP_WORLD_URL },
      ],
    },
  },

  founderQuote: {
    tag: 'Founder',
    name: 'Glenn Sanford',
    role: 'Founder & Chairman\nAGNT, Inc.',
    photoSrc: '/next/assets/images/agnt-inc/glenn-sanford.webp',
    photoAlt: 'Glenn Sanford',
    segments: [
      { text: 'We built this company on a simple belief:' },
      { text: 'put people first', emphasis: true },
      { text: 'and the rest follows. That principle has' },
      { text: 'carried us through' },
      { text: 'every market cycle', emphasis: true },
      { text: 'and it defines who we are.' },
    ],
  },

  innovationInvestor: {
    eyebrow: 'Innovation & Technology',
    heading: 'One Platform. Infinite Possibility.',
    body:
      'AGNT and our companies are reimagining how real estate operates connecting agents, franchise owners, and team leaders through technology built for the future.',
    cta: { label: 'Learn More', href: '/why-agnt/innovation-and-technology' },
    rowsLink: { label: 'View All Financials', href: FINANCIALS_URL },
    rows: [
      {
        tag: 'Stock Information',
        kind: 'stock',
        // Fallback copy shown only when the live quote is unavailable — the
        // section fills in the real price + delay note when SSR provides one.
        title: 'Live quote coming soon',
        subtitle: 'AGNT · NYSE/Nasdaq',
        href: '/stock-info', // TODO: replace with internal route when /stock-info page is built
      },
      {
        tag: 'Reports & Presentations',
        title: 'Q2 2026 Earnings Presentation',
        subtitle: 'Quarterly results, strategic guidance, and full financial detail.',
        href: Q2_DECK_URL,
      },
      {
        tag: 'Financial Calendar',
        title: 'Upcoming Events & Presentations',
        subtitle: 'Stay current with earnings dates, annual meetings, and conferences.',
        href: EVENTS_URL,
      },
    ],
  },

  news: {
    eyebrow: 'Corporate News',
    heading: 'Latest Financial & Corporate News',
    viewAll: { label: 'View All News', href: '/news' },
  },

  careersIntro: {
    eyebrow: 'Careers',
    heading: "Come build what's next.",
    body: 'AGNT, Inc. is where bold ideas meet real opportunity. Join a team rewriting the rules of real estate and your career along with it.',
    cta: { label: 'View Open Roles', href:  '/careers' },
  },

  // Reuses the shared `CardStack` component with AGNT-owned imagery, extracted
  // from the design repo (zoocasa/exp-world-holdings-AGNT `images/card-0x-*.jpg`)
  // into `public/next/assets/images/agnt-inc/`. Dark cards (1–2) use light text;
  // light cards (3–4) use navy text.
  careers: {
    title: 'What Sets Us Apart',
    content: [
      {
        title: 'Freedom to Build Your Future',
        description:
          "At AGNT, you're not bound by borders or office walls. Our virtual model gives you the power to choose where, how, and when you build your business. Unlock your earning potential and create the lifestyle you've always envisioned — on your terms.",
        imageSrc: '/next/assets/images/agnt-inc/card-stack-freedom.jpg',
        backgroundColor: Colors.expSecondaryDarkNavy,
        textColor: Colors.white,
        imageAlt: 'Agent building their business with freedom and flexibility',
        imagePosition: 'center 35%',
        href:  '/why-agnt',
        ctaLabel: 'Learn more',
      },
      {
        title: 'A Model that Works for You',
        description:
          'At AGNT, our brands are built around agent success. Depending on model, that means access to revenue share, equity opportunities, and features designed to reward growth. Build wealth while you build your business.',
        imageSrc: '/next/assets/images/agnt-inc/card-stack-ownership.jpg',
        backgroundColor: Colors.darkNavyLight,
        textColor: Colors.white,
        imageAlt: 'Agents sharing in company ownership and equity',
        imagePosition: 'center 30%',
        href:  '/agnt-businesses',
        ctaLabel: 'Learn more',
      },
      {
        title: 'Growth Without Limits',
        description:
          "Whether you're brand-new or a seasoned pro, AGNT equips you with top-tier training, live masterminds, and on-demand coaching. With tens of thousands of agents worldwide and cutting-edge tech at your fingertips, there's no ceiling on your success.",
        imageSrc: '/next/assets/images/agnt-inc/card-stack-community.jpg',
        backgroundColor: Colors.lightGrayishBlue,
        textColor: Colors.expSecondaryDarkNavy,
        imageAlt: 'A community of agents celebrating together',
        imagePosition: 'center 60%',
        href:  '/careers',
        ctaLabel: 'Learn more',
      },
      {
        title: 'Community That Has Your Back',
        description:
          "You're never building alone. At AGNT, you're part of a global network of like-minded entrepreneurs who support, mentor, and celebrate each other. From day one, collaboration beats competition.",
        imageSrc: '/next/assets/images/agnt-inc/card-stack-growth.jpg',
        backgroundColor: Colors.lightGrayishBlue2,
        textColor: Colors.expSecondaryDarkNavy,
        imageAlt: 'Agent learning at a training and growth session',
        imagePosition: 'center 25%',
        href:  '/news',
        ctaLabel: 'Learn more',
      },
    ],
  },

  calendarAlerts: {
    scheduleEyebrow: 'Schedule',
    scheduleHeading: 'Financial Calendar',
    events: [],
    viewAll: { label: 'View All Events', href: EVENTS_URL },
    form: {
      eyebrow: 'Stay Informed',
      heading: 'Stay ahead with AGNT, Inc.',
      body: 'Press releases, earnings calls, and financial updates.',
      links: [
        { icon: 'email', label: 'Send us an email', href: AGNT_IR_MAILTO },
        { icon: 'linkedin', label: 'Follow on LinkedIn', href: 'https://www.linkedin.com/company/expworldholdings/' },
        { icon: 'careers', label: 'Careers', href:  '/careers' },
      ],
    },
  },
};
